import React, { useState, useEffect, useCallback } from 'react'
import { Grid, GridColumn } from '@progress/kendo-react-grid'
import { Button } from '@progress/kendo-react-buttons'
import { DropDownList, MultiSelect } from '@progress/kendo-react-dropdowns'
import { Input, Checkbox } from '@progress/kendo-react-inputs'
import { TimePicker } from '@progress/kendo-react-dateinputs'
import { Drawer, DrawerContent, DrawerNavigation } from '@progress/kendo-react-layout'
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs'
import { FaPlus, FaTimesCircle, FaPaperPlane, FaTrash, FaEdit } from 'react-icons/fa'
import {
  getReportSchedules,
  getReportScheduleOptions,
  createReportSchedule,
  updateReportSchedule,
  deleteReportSchedule,
  sendReportScheduleNow,
  reportSchedule,
  reportScheduleOptions,
  reportScheduleOption,
  reportScheduleRecipientOption,
  reportScheduleForm,
} from '../../data/reportScheduleData'
import { useToast } from '../../contexts/ToastContext'
import './ReportSchedules.css'

const DAYS_OF_WEEK = [
  { value: 1, text: 'Monday' },
  { value: 2, text: 'Tuesday' },
  { value: 3, text: 'Wednesday' },
  { value: 4, text: 'Thursday' },
  { value: 5, text: 'Friday' },
  { value: 6, text: 'Saturday' },
  // Sunday is 0 in DayOfWeek, but a working week reads better ending on it.
  { value: 0, text: 'Sunday' },
]

// Capped at 28 so a monthly schedule never skips February.
const DAYS_OF_MONTH = Array.from({ length: 28 }, (_, i) => ({ value: i + 1, text: `${i + 1}` }))

const EMPTY_FORM: reportScheduleForm = {
  name: '',
  reportKey: '',
  frequency: 'Daily',
  dayOfWeek: 1,
  dayOfMonth: 1,
  hourOfDay: 8,
  minuteOfHour: 0,
  rangeKey: null,
  isActive: true,
  recipientEmployeeIds: [],
  recipientGroupIds: [],
}

const formatDateTime = (value: string | null) =>
  value ? new Date(value).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : ''

function ReportSchedules() {
  const { showToast } = useToast()

  const [schedules, setSchedules] = useState<reportSchedule[]>([])
  const [options, setOptions] = useState<reportScheduleOptions | null>(null)
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<reportScheduleForm>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<reportSchedule | null>(null)

  const loadSchedules = useCallback(async () => {
    try {
      setLoading(true)
      setSchedules(await getReportSchedules())
    } catch (error: any) {
      console.error('Failed to load report schedules:', error)
      showToast('Unable to load your schedules', 'error')
    } finally {
      setLoading(false)
    }
  }, [showToast])

  useEffect(() => {
    loadSchedules()
  }, [loadSchedules])

  useEffect(() => {
    getReportScheduleOptions()
      .then(setOptions)
      .catch((error) => {
        console.error('Failed to load schedule options:', error)
        showToast('Unable to load the list of reports', 'error')
      })
  }, [showToast])

  // Only the reports this user may see come back from the API, so an empty list
  // means they have no reports to schedule rather than a loading failure.
  const reports = options?.reports ?? []
  const ranges = options?.ranges ?? []
  const frequencies = options?.frequencies ?? []
  // Administrators only - for everybody else the pickers are not rendered at
  // all, and the API refuses recipients from them regardless.
  const canChooseRecipients = options?.canChooseRecipients ?? false
  const employees = options?.employees ?? []
  const groups = options?.groups ?? []

  const selectedReport = reports.find((r) => r.key === form.reportKey) ?? null
  // Snapshot reports have no period, so the picker is hidden rather than
  // offering a choice that would be ignored.
  const needsRange = selectedReport?.needsDateRange ?? true

  const openCreate = () => {
    setEditingId(null)
    setForm({
      ...EMPTY_FORM,
      reportKey: reports[0]?.key ?? '',
      rangeKey: ranges[0]?.key ?? null,
    })
    setExpanded(true)
  }

  const openEdit = (schedule: reportSchedule) => {
    setEditingId(schedule.reportScheduleId)
    setForm({
      name: schedule.name,
      reportKey: schedule.reportKey,
      frequency: schedule.frequency,
      dayOfWeek: schedule.dayOfWeek ?? 1,
      dayOfMonth: schedule.dayOfMonth ?? 1,
      hourOfDay: schedule.hourOfDay,
      minuteOfHour: schedule.minuteOfHour,
      rangeKey: schedule.rangeKey ?? ranges[0]?.key ?? null,
      isActive: schedule.isActive,
      recipientEmployeeIds: schedule.recipientEmployeeIds ?? [],
      recipientGroupIds: schedule.recipientGroupIds ?? [],
    })
    setExpanded(true)
  }

  const closeDrawer = () => {
    setExpanded(false)
    setEditingId(null)
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      const payload: reportScheduleForm = {
        ...form,
        // Cleared rather than sent as stale values the server would ignore.
        dayOfWeek: form.frequency === 'Weekly' ? form.dayOfWeek : null,
        dayOfMonth: form.frequency === 'Monthly' ? form.dayOfMonth : null,
        rangeKey: needsRange ? form.rangeKey : null,
        // Never sent by a non-administrator: the API refuses them, and sending
        // them would turn a harmless UI state into a failed save.
        recipientEmployeeIds: canChooseRecipients ? form.recipientEmployeeIds : [],
        recipientGroupIds: canChooseRecipients ? form.recipientGroupIds : [],
      }

      if (editingId === null) {
        await createReportSchedule(payload)
        showToast('Schedule created', 'success')
      } else {
        await updateReportSchedule(editingId, payload)
        showToast('Schedule updated', 'success')
      }

      closeDrawer()
      await loadSchedules()
    } catch (error: any) {
      console.error('Failed to save schedule:', error)
      // The API returns the reason a schedule was rejected, which is more use
      // than a generic failure.
      showToast(error.response?.data?.message || 'Unable to save the schedule', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return

    try {
      await deleteReportSchedule(deleteTarget.reportScheduleId)
      showToast('Schedule deleted', 'success')
      setDeleteTarget(null)
      await loadSchedules()
    } catch (error: any) {
      console.error('Failed to delete schedule:', error)
      showToast('Unable to delete the schedule', 'error')
      setDeleteTarget(null)
    }
  }

  const handleSendNow = async (schedule: reportSchedule) => {
    try {
      showToast('Building and sending the report…', 'info')
      const status = await sendReportScheduleNow(schedule.reportScheduleId)
      showToast(status, 'success')
      await loadSchedules()
    } catch (error: any) {
      console.error('Failed to send report:', error)
      showToast(error.response?.data?.message || 'Unable to send the report', 'error')
    }
  }

  const timeValue = (() => {
    const d = new Date()
    d.setHours(form.hourOfDay, form.minuteOfHour, 0, 0)
    return d
  })()

  return (
    <div>
      <div style={{ padding: '1rem' }}>
        <div className="report-schedules__header">
          <h1>Report Schedules</h1>
        </div>

        <div className="report-schedules__toolbar">
          <Button onClick={openCreate} themeColor="primary" disabled={reports.length === 0}>
            <FaPlus style={{ marginRight: '0.4rem' }} />
            New Schedule
          </Button>
          {options?.deliveryEmail && (
            <span className="report-schedules__delivery">
              Reports are sent to <strong>{options.deliveryEmail}</strong>
            </span>
          )}
          {reports.length === 0 && (
            <span className="report-schedules__delivery">
              You do not have access to any reports that can be scheduled.
            </span>
          )}
        </div>

        <div className="report-schedules-grid">
          <Grid data={schedules} total={schedules.length}>
            <GridColumn field="name" title="Name" />
            <GridColumn field="reportName" title="Report" width="190px" />
            <GridColumn field="summary" title="Schedule" width="230px" />
            <GridColumn
              field="rangeName"
              title="Period"
              width="170px"
              cell={(props) => <td>{props.dataItem.rangeName || 'Snapshot'}</td>}
            />
            <GridColumn
              field="recipientSummary"
              title="Sent To"
              width="190px"
            />
            <GridColumn
              field="isActive"
              title="Active"
              width="90px"
              cell={(props) => (
                <td>
                  <span
                    className={
                      props.dataItem.isActive
                        ? 'report-schedules__pill report-schedules__pill--on'
                        : 'report-schedules__pill report-schedules__pill--off'
                    }
                  >
                    {props.dataItem.isActive ? 'Yes' : 'No'}
                  </span>
                </td>
              )}
            />
            <GridColumn
              field="nextRunDate"
              title="Next Run"
              width="140px"
              cell={(props) => <td>{formatDateTime(props.dataItem.nextRunDate)}</td>}
            />
            <GridColumn
              field="lastRunDate"
              title="Last Run"
              width="200px"
              cell={(props) => (
                <td>
                  {formatDateTime(props.dataItem.lastRunDate)}
                  {props.dataItem.lastStatus && (
                    <div
                      className={
                        String(props.dataItem.lastStatus).startsWith('Failed')
                          ? 'report-schedules__status report-schedules__status--bad'
                          : 'report-schedules__status'
                      }
                    >
                      {props.dataItem.lastStatus}
                    </div>
                  )}
                </td>
              )}
            />
            <GridColumn
              title="Actions"
              width="210px"
              cell={(props) => (
                <td>
                  <Button size="small" onClick={() => openEdit(props.dataItem)} title="Edit">
                    <FaEdit />
                  </Button>{' '}
                  <Button
                    size="small"
                    onClick={() => handleSendNow(props.dataItem)}
                    title="Send now"
                  >
                    <FaPaperPlane />
                  </Button>{' '}
                  <Button
                    size="small"
                    onClick={() => setDeleteTarget(props.dataItem)}
                    title="Delete"
                  >
                    <FaTrash />
                  </Button>
                </td>
              )}
            />
          </Grid>
          {!loading && schedules.length === 0 && (
            <div className="report-schedules__empty">
              You have no report schedules yet.
            </div>
          )}
        </div>
      </div>

      {/* Create / edit */}
      <Drawer
        expanded={expanded}
        mode="overlay"
        position="end"
        width={520}
        onOverlayClick={closeDrawer}
      >
        <DrawerContent>
          <DrawerNavigation>
            <div className="report-schedules__drawer">
              <div className="report-schedules__drawer-head">
                <h2>{editingId === null ? 'New Schedule' : 'Edit Schedule'}</h2>
                <FaTimesCircle onClick={closeDrawer} style={{ cursor: 'pointer' }} />
              </div>

              <div className="report-schedules__field">
                <label>Name</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: String(e.value ?? '') })}
                  placeholder="Monday pipeline"
                  style={{ width: '100%' }}
                />
              </div>

              <div className="report-schedules__field">
                <label>Report</label>
                <DropDownList
                  data={reports}
                  textField="displayName"
                  dataItemKey="key"
                  value={reports.find((r) => r.key === form.reportKey) ?? null}
                  onChange={(e) =>
                    setForm({ ...form, reportKey: (e.value as reportScheduleOption).key })
                  }
                  style={{ width: '100%' }}
                />
              </div>

              {needsRange && (
                <div className="report-schedules__field">
                  <label>Period covered</label>
                  <DropDownList
                    data={ranges}
                    textField="displayName"
                    dataItemKey="key"
                    value={ranges.find((r) => r.key === form.rangeKey) ?? null}
                    onChange={(e) =>
                      setForm({ ...form, rangeKey: (e.value as reportScheduleOption).key })
                    }
                    style={{ width: '100%' }}
                  />
                </div>
              )}

              <div className="report-schedules__field">
                <label>Frequency</label>
                <DropDownList
                  data={frequencies}
                  value={form.frequency}
                  onChange={(e) => setForm({ ...form, frequency: String(e.value) })}
                  style={{ width: '100%' }}
                />
              </div>

              {form.frequency === 'Weekly' && (
                <div className="report-schedules__field">
                  <label>Day of week</label>
                  <DropDownList
                    data={DAYS_OF_WEEK}
                    textField="text"
                    dataItemKey="value"
                    value={DAYS_OF_WEEK.find((d) => d.value === form.dayOfWeek) ?? DAYS_OF_WEEK[0]}
                    onChange={(e) => setForm({ ...form, dayOfWeek: e.value.value })}
                    style={{ width: '100%' }}
                  />
                </div>
              )}

              {form.frequency === 'Monthly' && (
                <div className="report-schedules__field">
                  <label>Day of month</label>
                  <DropDownList
                    data={DAYS_OF_MONTH}
                    textField="text"
                    dataItemKey="value"
                    value={DAYS_OF_MONTH.find((d) => d.value === form.dayOfMonth) ?? DAYS_OF_MONTH[0]}
                    onChange={(e) => setForm({ ...form, dayOfMonth: e.value.value })}
                    style={{ width: '100%' }}
                  />
                  <span className="report-schedules__hint">
                    1 to 28, so the schedule never skips February.
                  </span>
                </div>
              )}

              <div className="report-schedules__field">
                <label>Time of day</label>
                <TimePicker
                  value={timeValue}
                  format="HH:mm"
                  onChange={(e) =>
                    setForm({
                      ...form,
                      hourOfDay: e.value ? e.value.getHours() : 0,
                      minuteOfHour: e.value ? e.value.getMinutes() : 0,
                    })
                  }
                  width="100%"
                />
                <span className="report-schedules__hint">
                  Sent within a few minutes of this time.
                </span>
              </div>

              {canChooseRecipients && (
                <>
                  <div className="report-schedules__field">
                    <label>Send to (people)</label>
                    <MultiSelect
                      data={employees}
                      value={employees.filter((e) =>
                        form.recipientEmployeeIds.includes(e.id)
                      )}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          recipientEmployeeIds: (e.value as reportScheduleRecipientOption[])
                            .map((x) => x.id),
                        })
                      }
                      textField="name"
                      dataItemKey="id"
                      placeholder="Leave empty to send to yourself"
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div className="report-schedules__field">
                    <label>Send to (groups)</label>
                    <MultiSelect
                      data={groups}
                      value={groups.filter((g) => form.recipientGroupIds.includes(g.id))}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          recipientGroupIds: (e.value as reportScheduleRecipientOption[])
                            .map((x) => x.id),
                        })
                      }
                      textField="name"
                      dataItemKey="id"
                      placeholder="Select groups..."
                      style={{ width: '100%' }}
                    />
                    <span className="report-schedules__hint">
                      A group is resolved to its members each time the report is
                      sent, so it follows people joining and leaving. Name nobody
                      and it comes to you.
                    </span>
                  </div>
                </>
              )}

              <div className="report-schedules__field">
                <Checkbox
                  value={form.isActive}
                  label="Active"
                  onChange={(e) => setForm({ ...form, isActive: Boolean(e.value) })}
                />
              </div>

              <div className="report-schedules__actions">
                <Button onClick={closeDrawer}>Cancel</Button>
                <Button themeColor="primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          </DrawerNavigation>
        </DrawerContent>
      </Drawer>

      {deleteTarget && (
        <Dialog title="Delete schedule" onClose={() => setDeleteTarget(null)}>
          <p style={{ margin: '1rem 0' }}>
            Delete "{deleteTarget.name}"? It will stop sending.
          </p>
          <DialogActionsBar>
            <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button themeColor="primary" onClick={handleDelete}>
              Delete
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  )
}

export default ReportSchedules
